/**
 * BrowserScreenshotPicker Hook
 *
 * Backs every "Import from a screenshot" button (the getting-started lead-in,
 * the composer's plus menu, and the choice buttons the assistant offers). Opens
 * the attachment file picker restricted to a single image, and tells the server
 * to expect the next upload to be a browser screenshot rather than an ordinary
 * attachment.
 *
 * ## One picker, mounted once
 *
 * This hook is mounted on a permanently-present hidden element
 * (`#browser-screenshot-picker` in the LiveView's template) and opens the
 * chooser when that element receives `brainbox:open-screenshot-picker`. It is
 * NOT mounted on the buttons, and that is the whole design:
 *
 *   - The screenshot choice now stays unanswered until a real file lands, which
 *     is what lets Cancel restore the same screen. The picker still must not be
 *     owned by that transient button: successful upload retires it, and any
 *     render while the chooser is open must not take away the window-focus
 *     listener below — the only way we can tell the user backed out.
 *   - The narrowing of the shared file input has to be undone by whoever did it.
 *     A hook that dies mid-chooser cannot.
 *
 * Cancel detection therefore has to outlive any button, and here it outlives
 * everything short of the view itself.
 *
 * The picker MUST be opened straight from the real click. Browsers only open a
 * file chooser while there is transient user activation, and a server round-trip
 * loses it — an earlier version pushed the click to the LiveView and had it push
 * back an "open the picker" event, which the browser silently ignored every
 * time. `JS.dispatch` from the button's own `phx-click` dispatches synchronously
 * inside that click, so the activation survives the hand-off to this element and
 * the chooser opens. The server is told in parallel.
 *
 * The `accept`/`multiple` narrowing is cosmetic — it constrains the chooser
 * only, and the server validates whatever arrives regardless. Both are restored
 * the moment the chooser closes, because the same input backs ordinary "Upload
 * files", which takes several files of many types.
 */

/**
 * The event that opens the picker. Dispatched at this hook's element by every
 * screenshot door — see `MessageItem.open_screenshot_picker/0`, which is where
 * the Elixir side of this contract is written down.
 */
const OPEN_EVENT = 'brainbox:open-screenshot-picker'

/**
 * The label wrapping the attachment file input. We go via the label rather than
 * the input's own id because `live_file_input` uses the upload ref as its id and
 * discards any id we pass it — looking the input up by name silently found
 * nothing. The rest of the app does the same (see the empty-state "Chat with
 * file" button).
 */
const FILE_INPUT_LABEL_ID = 'file-input-label'

/**
 * Image types the SERVER accepts (`allow_upload(:files, accept: …)`).
 *
 * Deliberately this list and not `image/*`: the chooser must not offer a type
 * the upload will reject, because a client-side rejection never reaches
 * `handle_progress/3` — the screenshot would simply never send, and the server
 * would sit armed waiting for it. Keep in step with the server's accept list.
 */
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp'

/** Grace period for `change` to land before we treat the chooser as cancelled. */
const CANCEL_GRACE_MS = 500

const BrowserScreenshotPicker = {
  mounted () {
    this.handleOpen = () => this.openPicker()
    this.el.addEventListener(OPEN_EVENT, this.handleOpen)
  },

  destroyed () {
    this.el.removeEventListener(OPEN_EVENT, this.handleOpen)
    // Only reachable when the whole view goes away, but a chooser open at that
    // moment still leaves the input narrowed and a focus listener live. Put both
    // back — the input outlives this hook and is shared with ordinary uploads.
    this.restoreInput()
    if (this.onWindowFocus) {
      window.removeEventListener('focus', this.onWindowFocus)
      this.onWindowFocus = null
    }
  },

  openPicker () {
    // Guard re-entry. Without it a second click captures the ALREADY-narrowed
    // attributes as the ones to restore, permanently narrowing the shared input
    // for the rest of the session.
    if (this.pickerOpen) { return }

    const label = document.getElementById(FILE_INPUT_LABEL_ID)
    const input = label?.querySelector('input[type="file"]')
    if (!input) {
      console.error('[BrowserScreenshotPicker] file input not found')
      return
    }

    // Arm the server first. The user still has a file chooser to get through,
    // so this lands well before the upload does.
    this.pushEvent('arm_browser_screenshot', {})

    this.pickerOpen = true
    this.input = input
    this.previousAccept = input.getAttribute('accept')
    this.previousMultiple = input.multiple
    input.setAttribute('accept', IMAGE_ACCEPT)
    // One screenshot. The send consumes every entry at once, so a second file
    // would either ride along in the same turn or trip the "still in progress"
    // guard on the server.
    input.multiple = false

    let picked = false
    // Chromium can emit `change` when the chooser closes even though its first
    // entry is null (Cancel). The event itself is therefore not proof that a
    // screenshot was selected; capture the file while it is still present.
    const onChange = () => { picked = Boolean(input.files?.[0]) }
    input.addEventListener('change', onChange, { once: true })

    // `focus` comes back to the window whether the user picked a file or
    // cancelled. Restore the input either way, then give `change` a moment to
    // land — if it never does they backed out, and the server has to be
    // disarmed or their next ordinary upload would be taken for a screenshot.
    this.onWindowFocus = () => {
      this.onWindowFocus = null
      this.restoreInput()

      setTimeout(() => {
        if (!picked) {
          input.removeEventListener('change', onChange)
          this.pushEvent('disarm_browser_screenshot', {})
        }
      }, CANCEL_GRACE_MS)
    }
    window.addEventListener('focus', this.onWindowFocus, { once: true })

    input.click()
  },

  /** Puts the shared file input back as we found it. Safe to call twice. */
  restoreInput () {
    if (!this.pickerOpen || !this.input) { return }

    if (this.previousAccept === null) {
      this.input.removeAttribute('accept')
    } else {
      this.input.setAttribute('accept', this.previousAccept)
    }
    this.input.multiple = this.previousMultiple

    this.pickerOpen = false
    this.input = null
  }
}

export default BrowserScreenshotPicker
